import { Button, Card, Flex, Heading, Stack, useToast } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useQueryClient } from '@tanstack/react-query';
import InputCopyField from 'components/form-elements/InputCopyField';
import AlertModal from 'components/modals/AlertModal';
import localConfig from 'config';
import { useAxios } from 'context/AxiosContextProvider';
import { useUserContext } from 'context/UserContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import useAssignNextTaskMutation from 'hooks/mutations/useAssignNextTaskMutation';
import { useDeleteB2CServiceDocument } from 'hooks/mutations/useDeleteB2CServiceDocumentMutation';
import useUpdateB2CServiceHistoryRequestMutation from 'hooks/mutations/useUpdateB2CServiceHistoryRequestMutation';
import useAccountV2Query from 'hooks/useAccountV2Query';
import useB2CServiceHistoryRequestQuery from 'hooks/useB2CServiceHistoryRequestQuery';
import { get } from 'lodash';
import TaskBannerCard from 'pages/tasks/TaskBannerCard';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCopyToClipboard } from 'usehooks-ts';

import { Activity, DetailPageTask, LooseObject, ServiceActivity, Task } from '../../api/types';
import ActivityLogCard from '../../components/ActivityLogCard';
import { ServiceHistoryRequestIcon } from '../../components/Icons';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { TasksTableCard } from '../../components/TasksTableCard';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { displayRow } from '../../helpers/general';
import useAssignMutation from '../../hooks/mutations/useAssignMutation';
import { useAttachFallbackEmptyB2CServiceHistoryMutation } from '../../hooks/mutations/useAttachFallbackEmptyB2CServiceHistoryMutation';
import useUploadB2CFileMutation from '../../hooks/mutations/useUploadB2CFileMutation';
import { ContentCard } from '../../layout/ContentCard';
import { ContentLayout } from '../../layout/ContentLayout';
import FileDisplay from '../FileDisplay';
import FileForm from '../FileForm';
import CredentialCard from '../workshop/CredentialCard';
import { B2CServiceHistoryStatusCard } from './components/B2CServiceHisotryStatusCard';

const B2CServiceHistoryDetailsPage = () => {
  const { user, isMember, opUsers } = useUserContext();
  const { t } = useTranslation();
  const toast = useToast();
  const axios = useAxios();
  const { id, type } = useParams();
  const navigate = useNavigate();
  const [canEdit, setCanEdit] = useState(false);
  const queryClient = useQueryClient();

  const serviceHistoryId = Number(id);

  const [value, copy] = useCopyToClipboard();

  const location = useLocation();
  const fromApprovedVerification = location.state?.fromApprovedVerification;

  const serviceHistoryQuery = useB2CServiceHistoryRequestQuery(serviceHistoryId);
  const queryToInvalidate = () => queryClient.invalidateQueries({ queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId) });
  const accountQuery = useAccountV2Query(
    serviceHistoryQuery.data?.data?.account_id ? parseInt(serviceHistoryQuery.data?.data?.account_id) : undefined,
  );
  const tasks = (serviceHistoryQuery.data?.data as LooseObject | undefined)?.tasks as DetailPageTask[] | undefined;

  const attachFallbackMutation = useAttachFallbackEmptyB2CServiceHistoryMutation(serviceHistoryId);

  const sendRequest = async ({ url, method = 'put', dataForEndpoint = {} }: { url: string; method?: string; dataForEndpoint?: any }) => {
    const axiosCall =
      method !== 'delete'
        ? // @ts-ignore
          axios[method](url, dataForEndpoint)
        : // @ts-ignore
          axios[method](url);
    return await axiosCall
      .catch((err: { response: { status: number } }) => {
        manageErrorResponse(toast, err.response);
        return Promise.reject(err);
      })
      // @ts-expect-error
      .then(({ data }) => {
        queryClient.invalidateQueries({ queryKey: useAccountV2Query.getKey(accountQuery.data?.id as number) });
        queryClient.invalidateQueries({
          queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId),
        });
        return data;
      });
  };

  const apiUrl = `${localConfig.apiBaseUrl}/service-history-request/${(serviceHistoryQuery?.data as unknown as { id: number })?.id}`;

  const serviceHistoryDocumentId = (serviceHistoryQuery.data?.data as unknown as ServiceActivity)?.service?.documents?.filter(
    (document) => document?.type === 'service_history',
  )[0]?.id;

  const deleteDocumentMutation = useDeleteB2CServiceDocument(serviceHistoryDocumentId);
  const uploadFileMutation = useUploadB2CFileMutation(serviceHistoryId);
  const updateServiceHistoryRequestStatus = useUpdateB2CServiceHistoryRequestMutation(serviceHistoryId);

  const serviceHistoryOpenTask = (serviceHistoryQuery.data?.data as LooseObject)?.open_task as Task;

  const hasOpenTask = serviceHistoryOpenTask != undefined;
  const assignMutation = useAssignMutation(serviceHistoryOpenTask?.id as number, useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId));

  const assignNextTaskMutation = useAssignNextTaskMutation(type ?? '');

  const handleAttachFallback = () => {
    NiceModal.show(AlertModal as any, {
      onSubmit: () => {
        attachFallbackMutation.mutate(undefined, {
          onSuccess: () => {
            toast({
              title: 'Success!',
              description: t('b2c_service_history_request:fallback.success'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            queryClient.invalidateQueries({
              queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId),
            });
          },
          onError: (error) => {
            manageErrorResponse(toast, error as unknown as string);
          },
        });
      },
      children: t('b2c_service_history_request:fallback.modal.attach_fallback_warning'),
      content: {
        header: t('b2c_service_history_request:fallback.modal.attach_fallback_confirm'),
        footer: {
          buttons: {
            cancelCaption: t('common:cancel'),
            actionCaption: t('b2c_service_history_request:fallback.label'),
          },
        },
      },
    });
  };

  useEffect(() => {
    if (
      fromApprovedVerification === true &&
      !serviceHistoryOpenTask?.assignee?.id &&
      (serviceHistoryQuery.data?.data as unknown as { open_task: Task | null })?.open_task !== null &&
      serviceHistoryQuery.data?.data?.status === 'submitted'
    ) {
      assignMutation.mutate(
        {
          updated_at: get(serviceHistoryQuery.data?.data, 'open_task.updated_at', serviceHistoryQuery.data?.data?.updated_at)!,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId) });
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceHistoryQuery.isFetching]);

  if (!serviceHistoryId || isNaN(serviceHistoryId) || serviceHistoryQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  if (accountQuery.isLoading || serviceHistoryQuery.isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {hasOpenTask ? (
        <TaskBannerCard
          queryToInvalidate={queryToInvalidate}
          opUsers={opUsers}
          currentUserId={user?.id}
          assignee={serviceHistoryOpenTask?.assignee}
          isLoading={serviceHistoryQuery.isFetching}
          assign={() =>
            assignMutation.mutate(
              {
                updated_at: get(serviceHistoryQuery.data?.data, 'open_task.updated_at', serviceHistoryQuery.data?.data?.updated_at)!,
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId) });
                },
              },
            )
          }
          reassign={(user_id) =>
            assignMutation.mutate(
              {
                force: true,
                user_id,
                updated_at: get(serviceHistoryQuery.data?.data, 'open_task.updated_at', serviceHistoryQuery.data?.data?.updated_at)!,
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({ queryKey: useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId) });
                },
              },
            )
          }
          task={serviceHistoryOpenTask as Task}
        />
      ) : null}

      <ContentLayout
        sidebar={
          <>
            {type ? (
              <Button
                variant="primary"
                disabled={serviceHistoryQuery.isFetching || !isMember('tasks:assign-next')}
                data-test-id={`service_history_request_assign_button`}
                onClick={() =>
                  assignNextTaskMutation.mutate(undefined, {
                    onSuccess: (data) => {
                      if (data?.id) {
                        navigate(`/app/op/b2c-service-history-request/${data.subject_id}/${type}`);
                      } else {
                        navigate(`/app/op/break`);
                      }
                    },
                  })
                }
              >
                {t('common:next_activity')}
              </Button>
            ) : null}
            <Card p={4}>
              {displayRow({
                label: t('workshop:email.label'),
                content: serviceHistoryQuery.data?.data?.email ?? '-',
              })}
            </Card>
            {serviceHistoryQuery.data?.data?.account !== null && (
              <CredentialCard
                credential={accountQuery.data}
                isLoading={serviceHistoryQuery.isFetching}
                // @ts-ignore
                queryKey={useAccountV2Query.getKey()}
                sendRequest={(formData) =>
                  sendRequest({
                    url: `${localConfig.apiBaseUrl}/account/${accountQuery.data?.id}`,
                    dataForEndpoint: formData,
                  })
                }
              />
            )}
          </>
        }
      >
        <B2CServiceHistoryStatusCard
          permission="service-history-requests:update"
          canEdit={serviceHistoryQuery.data?.data?.status === 'processing'}
          completedAt={dateTimeTransformer(((serviceHistoryQuery.data?.data as LooseObject)?.completed_at as string) ?? '')}
          currentStatus={serviceHistoryQuery.data?.data?.status ?? ''}
          id={serviceHistoryId}
          isLoading={serviceHistoryQuery.isFetching}
          reasons={['payed', 'rejected']}
          shouldHaveFile={true}
          updateStatus={(data) => {
            const statusInfo = { status: data.status };
            updateServiceHistoryRequestStatus.mutate(statusInfo, {
              onError(error) {
                manageErrorResponse(toast, error as unknown as string);
              },
            });
          }}
          assignee={serviceHistoryOpenTask?.assignee}
        />

        <ContentCard
          isLoading={serviceHistoryQuery.isFetching}
          icon={<ServiceHistoryRequestIcon boxSize={10} />}
          minH="fit-content"
          header={`${t('b2c_service_history_request:service_history.label')}( ${serviceHistoryQuery.data?.data?.user_readable_id} )`}
        >
          <Stack w={'100%'} data-test-id="service-history-information">
            <Stack w="full" spacing={2}>
              <Heading size={'xxs'}>{t('b2c_service_history_request:details.master_data_title')}</Heading>
              <Heading size={'xxs'}>{t('forms:vehicle.label')}</Heading>
              <button type="button">
                <InputCopyField
                  copy={{
                    currentCopiedValue: value,
                    handleCopy: copy,
                  }}
                  field={{
                    value: serviceHistoryQuery.data?.data?.vin ?? '-',
                    label: t('forms:vin.label'),
                  }}
                  aria-label={'vin'}
                />
              </button>
            </Stack>
          </Stack>
        </ContentCard>
        <ContentCard header={t('b2c_service_history_request:details.pdf_title')}>
          <Stack w={'full'}>
            {!(serviceHistoryQuery.data?.data as LooseObject)?.file_id || canEdit ? (
              <FileForm
                deleteServiceDocumentMutation={deleteDocumentMutation}
                uploadFileMutation={uploadFileMutation}
                serviceDocumentExists={!!serviceHistoryDocumentId}
                setCanEdit={setCanEdit}
                editCancelButton={
                  (serviceHistoryQuery.data?.data as LooseObject)?.file_id && (
                    <Button onClick={() => setCanEdit(false)} variant={'outline'}>
                      {t('common:cancel')}
                    </Button>
                  )
                }
              />
            ) : (
              <FileDisplay
                apiUrl={apiUrl}
                handleReplace={() => setCanEdit(true)}
                record={serviceHistoryQuery.data?.data}
                canEdit={isMember('service-history-requests:update')}
                resourceKey={'b2c-service-history-request'}
                isV2={true}
              />
            )}
          </Stack>
          <Flex justifyContent="flex-end">
            <Button
              color="white"
              bgColor={'green.500'}
              size="md"
              isLoading={attachFallbackMutation.isPending}
              disabled={
                !isMember('service-history-requests:update') ||
                serviceHistoryQuery.data?.data?.status !== 'processing' ||
                Boolean(serviceHistoryOpenTask.user_id !== user?.id)
              }
              _hover={{ bg: 'green.400' }}
              onClick={handleAttachFallback}
            >
              {t('b2c_service_history_request:fallback.label')}
            </Button>
          </Flex>
        </ContentCard>
        <TasksTableCard tasks={tasks} isLoading={serviceHistoryQuery.isPending} />
        <ActivityLogCard
          resourceKey={'b2c_service_history_request'}
          queryKey={useB2CServiceHistoryRequestQuery.getKey(serviceHistoryId)}
          activities={(serviceHistoryQuery.data?.data as LooseObject)?.activities as Activity[]}
          account_id={serviceHistoryId}
        />
      </ContentLayout>
    </>
  );
};

export default B2CServiceHistoryDetailsPage;
